import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { HoverPopover } from '../ui/hover-popover';

/** Khoá i18n gốc của một biểu đồ: `chartHelp.<key>.{what,read,watch}`. */
export type ChartHelpKey = string;

/**
 * Nút (i) giải thích cách đọc một thứ — APM-CHART-a.
 *
 * Ra đời cho biểu đồ, và từ APM-OKR5 dùng cả ở chỗ khác (hàng số của một OKR):
 * ba câu hỏi `what` / `read` / `watch` đúng với mọi con số cần giải thích, và
 * dựng một cơ chế trợ giúp thứ hai là để hai cơ chế trôi khỏi nhau.
 *
 * ## Vì sao mỗi biểu đồ cần nó
 *
 * Tên biểu đồ nói nó **là gì**, không nói cách đọc. "CFD", "EVM", "Độ chính xác
 * ước lượng" đều là những cái tên mà người mở ra lần đầu không suy ra được: trục
 * nào là gì, con số bao nhiêu thì đáng lo, và thấy rồi thì làm gì tiếp. Không có
 * chỗ trả lời, người dùng hoặc đoán — rồi kết luận sai từ dữ liệu đúng — hoặc
 * bỏ qua cả biểu đồ.
 *
 * Nên nội dung khai theo **ba phần cố định**, không phải một đoạn văn tự do:
 * biểu đồ này cho biết gì (`what`), đọc thế nào (`read`), và khi nào thì đáng lo
 * (`watch`). Ba câu hỏi ấy là ba thứ người dùng thật sự hỏi, và một khuôn cố
 * định giữ cho biểu đồ thứ hai mươi ba được giải thích kỹ như biểu đồ đầu tiên.
 *
 * ## Mở bằng di chuột, **và** bằng bàn phím lẫn chạm
 *
 * Yêu cầu là "di chuột qua thì đọc được", nhưng chỉ-hover là bỏ rơi hai nhóm:
 * người dùng bàn phím không có con trỏ để rê, còn trên màn hình cảm ứng thì
 * không có trạng thái hover nào cả — nút sẽ im lặng không làm gì. Nên nó là một
 * `<button>` thật: hover mở, mà click và focus cũng mở.
 *
 * ⚠️ `onOpenAutoFocus` bị chặn khi mở bằng hover. Không chặn thì rê chuột ngang
 * qua biểu đồ sẽ **giật tiêu điểm** khỏi ô người dùng đang gõ — và họ không hề
 * bấm gì.
 */
export function ChartHelp({
  helpKey,
  label,
  headings = 'chart',
}: {
  readonly helpKey: ChartHelpKey;
  /**
   * Nhãn cho trình đọc màn hình. Mặc định nói về **biểu đồ**.
   *
   * ⚠️ Từ APM-OKR5 nút này còn dùng ngoài biểu đồ (hàng số của một OKR), và ở
   * đó "Cách đọc biểu đồ này" là một câu sai — người dùng bàn phím sẽ đi tìm
   * một biểu đồ không tồn tại. Chỗ nào không phải biểu đồ thì truyền nhãn của
   * chính nó.
   */
  readonly label?: string;
  /**
   * Tiêu đề ba phần. `chart` = "Biểu đồ này cho biết gì / Đọc thế nào / Khi nào
   * đáng lo"; `metric` = "Là gì / Đo thế nào / Lưu ý" — cho một **chỉ số** đứng
   * riêng (chỉ số đo tự động của OKR, 2026-09-30), nơi chữ "biểu đồ" là sai.
   */
  readonly headings?: 'chart' | 'metric';
}): ReactNode {
  const { t } = useTranslation();
  const sections = (['what', 'read', 'watch'] as const).flatMap((part) => {
    const key = `chartHelp.${helpKey}.${part}`;
    const text = t(key);
    // Khoá thiếu ⇒ `t()` trả về chính khoá. Hiện `chartHelp.evm.watch` ra màn
    // hình còn tệ hơn bỏ trống mục đó.
    return text === key ? [] : [{ part, text }];
  });

  if (sections.length === 0) return null;

  return (
    <HoverPopover
      label={label ?? t('chart.helpLabel')}
      icon={<Info aria-hidden className="size-4" />}
    >
      <dl className="flex flex-col gap-2">
        {sections.map((section) => (
          <div key={section.part} className="flex flex-col gap-0.5">
            <dt className="font-medium">
              {t(`chart.${headings === 'metric' ? 'metricHelp' : 'help'}.${section.part}`)}
            </dt>
            <dd className="text-muted-foreground">{emphasized(section.text)}</dd>
          </div>
        ))}
      </dl>
    </HoverPopover>
  );
}

/** `**đậm**` trong chuỗi i18n → `<strong>` — dùng chung cho mọi ô giải thích. */
export function emphasized(text: string): ReactNode[] {
  return text.split('**').map((part, index) =>
    index % 2 === 1 ? (
      // `key` theo chỉ số là an toàn ở đây: các mảnh của một chuỗi cố định,
      // chúng không đổi thứ tự và không thêm bớt.
      <strong key={index} className="font-medium text-foreground">
        {part}
      </strong>
    ) : (
      <span key={index}>{part}</span>
    ),
  );
}
